import React, { useState } from "react";
import { useAddUser } from "../hooks/useAddUser";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { addUserSchema } from "./addUserSchema"; 

const availableGroups = ["admin", "executive", "wholesale", "retail", "ecommerce"];

type AddUserForm = typeof addUserSchema._type;

const AddUser: React.FC = () => {
  const { mutate: addUser, status, reset } = useAddUser();
  const isLoading = status === "pending";
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset: resetForm,
    formState: { errors },
  } = useForm<AddUserForm>({
    resolver: zodResolver(addUserSchema),
    defaultValues: {
      username: "",
      email: "",
      groups: [],
    },
  });

  const selectedGroups = watch("groups");

  const handleGroupChange = (group: string) => {
    const current = selectedGroups || [];
    if (current.includes(group)) {
      setValue(
        "groups",
        current.filter((g) => g !== group)
      );
    } else {
      setValue("groups", [...current, group]);
    }
  };

  const onSubmit = (data: AddUserForm) => {
    setMessage(null);
    addUser(data, {
      onSuccess: () => {
        setMessage({ type: "success", text: "User added successfully!" });
        resetForm();
        reset();
      },
      onError: (err: any) => {
        setMessage({ type: "error", text: err?.message || "Failed to add user." });
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {message && (
        <div
          className={`p-2 rounded text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}
      <div>
        <label htmlFor="username" className="block text-sm font-medium">
          Username
        </label>
        <Input
          id="username"
          {...register("username")}
          placeholder="Enter username"
        />
        {errors.username && (
          <span className="text-xs text-red-600">{errors.username.message}</span>
        )}
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          {...register("email")}
          placeholder="Enter email"
        />
        {errors.email && (
          <span className="text-xs text-red-600">{errors.email.message}</span>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium">Groups</label>
        <div className="space-y-2">
          {availableGroups.map((group) => (
            <div key={group} className="flex items-center space-x-2">
              <Checkbox
                id={group}
                checked={selectedGroups?.includes(group) || false}
                onCheckedChange={() => handleGroupChange(group)}
              />
              <label htmlFor={group} className="text-sm">
                {group}
              </label>
            </div>
          ))}
        </div>
        {errors.groups && (
          <span className="text-xs text-red-600">{errors.groups.message as string}</span>
        )}
      </div>
      <Button type="submit" disabled={isLoading}>
        {isLoading ? "Adding..." : "Add User"}
      </Button>
    </form>
  );
};

export default AddUser;